import type { PublicLocationWithLanes } from '@clear-denver/tpc-bff-client/build/api/tpc-bff/types'
import { Enrollment } from '@clear-denver/tpc-openapi-typescript'
import {
  EnrollmentContext,
  useEnrollmentContext,
} from '@contexts/EnrollmentContext'
import { isAgeQualifiedForYoungAdult } from '@utils/dateDifference'
import { useCallback, useMemo } from 'react'
import { useFlag } from '../useFlag'
import { createPNELTelemetryPipe } from './telemetry'

export type ConfirmationPageVariant = `paid(${boolean})_booked(${boolean})`

type LocationPageNextRoutes =
  | '/enroll/payment'
  | '/enroll/book-appointment'
  | '/enroll/finalize/confirmation'
type LocationPageBackRoutes = '/enroll/history'

type CheckoutPageNextRoutes = '/enroll/book-appointment'
type CheckoutPageBackRoutes = '/enroll/locations'

type AppointmentPageNextRoutes = '/enroll/finalize/confirmation'
type AppointmentPageBackRoutes = '/enroll/locations' | '/enroll/payment'

type ConfirmationPageNextRoutes = ''
type ConfirmationPageBackRoutes = '' | '/enroll/book-appointment'

type NextRouteNav = {
  location: LocationPageNextRoutes
  checkout: CheckoutPageNextRoutes
  appointment: AppointmentPageNextRoutes
  confirmation: ConfirmationPageNextRoutes
}
type NextRouteResult<
  key extends keyof NextRouteNav,
  ReturnValue = NextRouteNav[key]
> = [true, ReturnValue] | [false, undefined]

type LocationRouteConditions = { isLocationAvailable: boolean | undefined }
type NextRouteOptions = {
  location: LocationRouteConditions
  checkout: undefined
  appointment: undefined
  confirmation: undefined
}

export type BackRouteNav = {
  location: LocationPageBackRoutes
  checkout: CheckoutPageBackRoutes
  appointment: AppointmentPageBackRoutes
  confirmation: ConfirmationPageBackRoutes
}
export type BackRouteResult<
  key extends keyof BackRouteNav,
  ReturnValue = BackRouteNav[key]
> = [true, ReturnValue] | [false, undefined]

type Overrides = Partial<{
  debugOverride: boolean
  enableOverride: boolean
  variantOverride: ConfirmationPageVariant
}>

/**
 * Feature slice that centralizes and computes logic to integrate with pages
 */
function usePNEL({
  enableOverride,
  variantOverride,
  debugOverride,
}: Overrides = {}) {
  /**
   * FIX: types are busted
   * - even if contract specifies undefined the value only indicates boolean
   */
  const { enabled: _enabled } = useFlag('prePay') ?? {}
  const { isPromoActive = false } = useFlag('youngAdultDiscount') ?? {}

  const isLoading = typeof _enabled !== 'boolean'
  const enabled = _enabled ?? false

  const {
    state: {
      id: enrollmentId,
      applicationId,
      documentCheck,
      biographicInfo,
      contact,
    },
    apptInfo,
    voucher,
  } = useEnrollmentContext()

  /** cannot deconstruct because of null vs. undefined defaults */
  const { locationAddress, convertedStartTime } = apptInfo ?? {}
  const { dateOfBirth, firstName, middleName, lastName } = biographicInfo ?? {}
  const { email } = contact ?? {}
  const { photoId: primaryDocId, document: secondaryDocId } =
    documentCheck ?? {}

  const isEnabledOnWebsite = useMemo(() => {
    return enableOverride ?? enabled
  }, [enableOverride, enabled])

  const isEnabledWithinFlow = useMemo(() => {
    return (
      enableOverride ??
      (isEnabledOnWebsite &&
        (!isPromoActive ||
          (isPromoActive && !isAgeQualifiedForYoungAdult(dateOfBirth))))
    )
  }, [enableOverride, isEnabledOnWebsite, dateOfBirth, isPromoActive])

  const isAppointmentBooked = useMemo(() => {
    return (
      (locationAddress ?? '').length > 0 &&
      (convertedStartTime ?? '').length > 0
    )
  }, [locationAddress, convertedStartTime])

  const isUsableVoucher = useCallback((x: unknown): x is Enrollment.Voucher => {
    return typeof x === 'object' && x !== null
  }, [])

  const isPrePaid = useMemo(() => {
    return isUsableVoucher(voucher)
  }, [voucher, isUsableVoucher])

  const variant = useMemo((): ConfirmationPageVariant => {
    return (
      variantOverride ??
      (`paid(${isPrePaid})_booked(${isAppointmentBooked})` as const)
    )
  }, [variantOverride, isAppointmentBooked, isPrePaid])

  const getNextRoute = useCallback(
    <Route extends keyof NextRouteNav>(
      currentRoute: Route,
      options: NextRouteOptions[Route]
    ): NextRouteResult<Route> => {
      if (currentRoute === 'location') {
        const { isLocationAvailable } = options
        const route: NextRouteNav['location'] = isEnabledWithinFlow
          ? '/enroll/payment'
          : isLocationAvailable
          ? '/enroll/book-appointment'
          : '/enroll/finalize/confirmation'
        return [true, route] as NextRouteResult<Route>
      }

      if (currentRoute === 'checkout') {
        const route: NextRouteNav['checkout'] = '/enroll/book-appointment'
        return [true, route] as NextRouteResult<Route>
      }

      if (currentRoute === 'appointment') {
        const route: NextRouteNav['appointment'] =
          '/enroll/finalize/confirmation'
        return [true, route] as NextRouteResult<Route>
      }

      if (currentRoute === 'confirmation') {
        const route: NextRouteNav['confirmation'] = ''
        return (
          route.length > 0 ? [true, route] : [false, undefined]
        ) as NextRouteResult<Route>
      }
    },
    [isEnabledWithinFlow]
  )

  const getPreviousRoute = useCallback(
    <Route extends keyof BackRouteNav>(
      currentRoute: Route
    ): BackRouteResult<Route> => {
      if (currentRoute === 'location') {
        const route: BackRouteNav['location'] = '/enroll/history'
        return [true, route] as BackRouteResult<Route>
      }

      if (currentRoute === 'checkout') {
        const route: BackRouteNav['checkout'] = '/enroll/locations'
        return [true, route] as BackRouteResult<Route>
      }

      if (currentRoute === 'appointment') {
        const route: BackRouteNav['appointment'] = isEnabledWithinFlow
          ? '/enroll/payment'
          : '/enroll/locations'
        return [true, route] as BackRouteResult<Route>
      }

      if (currentRoute === 'confirmation') {
        const route: BackRouteNav['confirmation'] = isAppointmentBooked
          ? ''
          : '/enroll/book-appointment'
        return (
          route.length > 0 ? [true, route] : [false, undefined]
        ) as BackRouteResult<Route>
      }
    },
    [isEnabledWithinFlow, isAppointmentBooked]
  )

  const getState = useCallback(
    (
      locations: PublicLocationWithLanes[] = [],
      data: Record<string, { value: string; label: string }[]> = {}
    ) => {
      const location = getLocationState(locations, apptInfo ?? {})

      const kvp: Record<string, string> = Object.values(data).reduce(
        (acc, el) => {
          el.forEach((x) => {
            acc[x.value] = x.label
          })
          return acc
        },
        {}
      )
      const primaryDocName = kvp[primaryDocId] || undefined
      const secondaryDoc = kvp[secondaryDocId] || undefined
      const documents =
        !primaryDocName && !secondaryDoc
          ? undefined
          : secondaryDoc
          ? `${primaryDocName}, ${secondaryDoc}`
          : `${primaryDocName}`

      const name = middleName
        ? `${firstName} ${middleName} ${lastName}`
        : `${firstName} ${lastName}`

      return {
        enrollmentId,

        /** biographics */
        name,
        email,
        dateOfBirth,

        /** what to bring */
        required: {
          applicationId: applicationId || undefined,
          documents,
          voucherCode: isPrePaid ? voucher.code : undefined,
        },

        /** appointment */
        appointment: isAppointmentBooked
          ? {
              location,
              time: convertedStartTime,
            }
          : { location: undefined, time: undefined },
      }
    },
    [
      applicationId,
      apptInfo,
      convertedStartTime,
      dateOfBirth,
      secondaryDocId,
      email,
      enrollmentId,
      firstName,
      isAppointmentBooked,
      isPrePaid,
      lastName,
      middleName,
      primaryDocId,
      voucher,
    ]
  )

  const pipe = useMemo(
    () => createPNELTelemetryPipe(debugOverride ?? false),
    [debugOverride]
  )

  return {
    /** computed */
    getState,

    isLoading,
    isEnabledOnWebsite,
    isEnabledWithinFlow,
    isAppointmentBooked,
    isPrePaid,
    isUsableVoucher,

    variant,

    getNextRoute,
    getPreviousRoute,

    /** telemetry */
    pipe,
  }
}

/** exported for test purposes, do not use directly */
export const getLocationState = (
  locations: PublicLocationWithLanes[] = [],
  { locationId }: Pick<EnrollmentContext['apptInfo'], 'locationId'> = {}
) => {
  if (typeof locationId !== 'string') {
    return {}
  }
  const uid = locationId.toLowerCase()
  const chosenLocation: PublicLocationWithLanes | undefined = locations.find(
    (location) => location.uid.toLowerCase() === uid
  )

  if (!chosenLocation) {
    return {}
  }

  const {
    type,
    name: id,
    address1,
    address2,
    city,
    state,
    longitude,
    latitude,
  } = chosenLocation

  let name = id
  if (id.endsWith(`, ${state}`)) {
    name = id.slice(0, name.length - (state.length + 2))
  }

  const address =
    (address2 ? `${address1}, ${address2}` : `${address1}`) +
    `, ${city}, ${state}`

  const href = `https://www.google.com/maps/dir/?api=1&destination=${encodeURI(
    `${name} ${address} ${latitude}, ${longitude}`
  )}`

  return {
    type,
    name,
    address,
    href,
  }
}

export default usePNEL
